/* @flow */

import type { Reconcilier } from '../../../../helpers/profiles/typesAndConstants';
import type { Profiles } from '../../../../helpers/profiles/typesAndConstants';
import type { NETGEM_API_V8_REQUEST_RESPONSE } from '../../../../libs/netgemLibrary/v8/types/RequestResponse';
import type { Dispatch } from '../../../types/types';
import type { RequestResponseMethodDefinitionType } from '../emitter';

import Localizer from '../../../../helpers/localization';
import { Messenger, MessengerEvents } from '../../../../helpers/messenger';
import ProfilesManager from '../../../../helpers/profiles/profilesManager';
import { CustomNetworkError } from '../../../../libs/netgemLibrary/helpers/CustomNetworkError';
import { PersonalDataKey } from '../../../../libs/netgemLibrary/personalData/constants/keys';
import { HttpStatus } from '../../../../libs/netgemLibrary/v8/constants/NetworkCodesAndMessages';
import { sendPersonalDataGetRequest } from './get';
import { sendPersonalDataPostRequest } from './post';

// Maximum number of attempts to update profiles before giving up (in case of persisting conflicts)
const MAX_ATTEMPTS = 3;

const getUserProfiles: (initialETag: string | null, signal?: AbortSignal) => RequestResponseMethodDefinitionType =
  (initialETag, signal) =>
  (dispatch: Dispatch): Promise<{ eTag: string | null, profiles?: Profiles }> =>
    dispatch(sendPersonalDataGetRequest(PersonalDataKey.UserProfiles, initialETag, signal))
      .then((response: NETGEM_API_V8_REQUEST_RESPONSE) => {
        const { eTag, result } = response;
        return { eTag, profiles: JSON.parse(result) };
      })
      .catch((error: CustomNetworkError) => {
        const status = error.getStatus();
        if (status === HttpStatus.NotFound) {
          // Not found means no user profiles yet
          return null;
        } else if (status === HttpStatus.NotModified) {
          return { eTag: initialETag };
        }

        throw error;
      });

const updateProfiles: (profiles: Profiles, reconcileFn: Reconcilier, initialEtag: string | null, signal?: AbortSignal, attempt?: number) => RequestResponseMethodDefinitionType =
  (profiles, reconcileFn, initialETag, signal, attempt = 1) =>
  (dispatch: Dispatch): Promise<Profiles> =>
    dispatch(sendPersonalDataPostRequest(PersonalDataKey.UserProfiles, profiles, initialETag, signal))
      .then((data: NETGEM_API_V8_REQUEST_RESPONSE) => {
        const { eTag } = data;
        ProfilesManager.setETag(eTag);
        return Promise.resolve(profiles);
      })
      .catch((error: CustomNetworkError) => {
        const status = error.getStatus();
        if (status === HttpStatus.Conflict || status === HttpStatus.PreconditionFailed || status === HttpStatus.PreconditionRequired) {
          /*
           * Most probable cause: another device paired with the same account already updated the profiles
           * So we send a GET request to retrieve the latest ETag, but we only try once to avoid infinite loop between multiple devices
           */
          if (attempt <= MAX_ATTEMPTS) {
            ProfilesManager.setETag(null);

            return dispatch(getUserProfiles(null, signal))
              .then(({ eTag: newETag, profiles: newProfiles }) => {
                ProfilesManager.setETag(newETag);

                // Re-apply local changes to newly retrieved profiles
                const mergedProfiles = reconcileFn(newProfiles);

                if (mergedProfiles !== null) {
                  // Local changes have been re-applied to new profiles: upload them
                  return dispatch(updateProfiles(mergedProfiles, reconcileFn, newETag, signal, attempt + 1));
                }

                // Local changes don't make sense any longer: return newly downloaded profiles
                return Promise.resolve(newProfiles);
              })
              .catch((error) => {
                // GET request failed: nothing to update
                Messenger.emit(MessengerEvents.NOTIFY_ERROR, Localizer.localize('profiles.errors.update_error'));
                throw error;
              });
          } else {
            // Maximum number of attempts reached: bail out
            Messenger.emit(MessengerEvents.NOTIFY_ERROR, Localizer.localize('profiles.errors.max_attempts_reached'));
          }
        }

        throw error;
      });

export { getUserProfiles, updateProfiles };
